import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, deleteDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../src/firebaseConfig';
import './ExpiringSubscriptions.css';
import styles from './PlatformAccounts.module.css';
import moment from 'moment-timezone';
import { useNavigate } from 'react-router-dom';
import backgroundImage from '../assets/background3.jpg'; 

function ExpiringSubscriptions() {
    const [accounts, setAccounts] = useState([]);
    const [sales, setSales] = useState([]);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const [selectedRow, setSelectedRow] = useState(null);

    const handleBack = () => {
        navigate('/home');
    };

    const backgroundStyle = {
        minHeight: '100vh',
        width: '100%',
        margin: 0,
        padding: 0,
        backgroundImage: `linear-gradient(
            rgba(0, 0, 0, 0.55), 
            rgba(0, 0, 0, 0.65)
        ), url(${backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        backgroundRepeat: 'no-repeat',
        position: 'relative',
        overflowX: 'hidden' 
    };

    const fetchExpiringData = async () => {
        setLoading(true);
        try {
            const today = moment().tz('America/Bogota').startOf('day');
            const thresholdDate = today.clone().add(5, 'days');

            // Fetch data from Ventas (Clásico)
            const salesRef = collection(db, 'Ventas');
            const expiringSalesQuery = query(salesRef, where('endDate', '>', today.format('YYYY-MM-DD')), where('endDate', '<=', thresholdDate.format('YYYY-MM-DD')));
            const expiredSalesQuery = query(salesRef, where('endDate', '<=', today.format('YYYY-MM-DD')));

            const [expiringSnapshot, expiredSnapshot] = await Promise.all([
                getDocs(expiringSalesQuery),
                getDocs(expiredSalesQuery)
            ]);

            const expiringSales = expiringSnapshot.docs.map(doc => ({ 
                id: doc.id, 
                ...doc.data(), 
                cuentaEstado: 'Próxima a vencer',
                tipoVenta: 'Clásico' // Agregar identificador
            }));
            const expiredSales = expiredSnapshot.docs.map(doc => ({ 
                id: doc.id, 
                ...doc.data(), 
                cuentaEstado: 'Vencida',
                tipoVenta: 'Clásico' // Agregar identificador
            }));

            const salesData = [...expiringSales, ...expiredSales];

            // Fetch data from VentasOnline (Online)
            const ventasOnlineRef = collection(db, 'ventasOnline');
            
            // Para ventasOnline usamos fechaCorte en formato DD/MM/YYYY
            const formatDateForComparison = (dateStr) => {
                return moment(dateStr, 'DD/MM/YYYY').format('YYYY-MM-DD');
            };

            // Obtener todas las ventasOnline activas
            const ventasOnlineQuery = query(ventasOnlineRef, where('activo', '==', true));
            const ventasOnlineSnapshot = await getDocs(ventasOnlineQuery);
            
            const ventasOnlineData = ventasOnlineSnapshot.docs.map(doc => {
                const data = doc.data();
                const fechaCorteFormatted = formatDateForComparison(data.fechaCorte);
                const daysUntilExpiry = moment(fechaCorteFormatted).diff(today, 'days');
                
                let estado = '';
                if (daysUntilExpiry < 0) {
                    estado = 'Vencida';
                } else if (daysUntilExpiry <= 5) {
                    estado = 'Próxima a vencer';
                }
                
                return {
                    id: doc.id,
                    ...data,
                    endDate: fechaCorteFormatted, // Normalizar formato de fecha
                    cuentaEstado: estado,
                    tipoVenta: 'Online',
                    // Mapear campos para consistencia
                    saleType: data.plataforma,
                    phoneNumber: data.numero,
                    quantity: data.tipoCuenta ? parseInt(data.tipoCuenta.match(/\d+/)?.[0] || '1') : 1,
                    perfil: data.nombrePerfil,
                    pin: data.pinPerfil
                };
            }).filter(venta => venta.cuentaEstado !== ''); // Solo incluir las que están por vencer o vencidas

            // Combinar ambos conjuntos de datos
            const allSalesData = [...salesData, ...ventasOnlineData];

            // Fetch data from Cuentas
            const accountsRef = collection(db, 'Cuentas');
            const accountsSnapshot = await getDocs(accountsRef);
            const accountsData = accountsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            const relatedData = allSalesData.map(sale => {
                const relatedAccount = accountsData.find(account => account.codigo === sale.codigo) || {};
            
                // El correo siempre viene de la colección Cuentas
                const correo = relatedAccount.correo || 'No disponible';
                
                // Email del usuario solo para ventasOnline
                const emailUser = sale.tipoVenta === 'Online' ? 
                    (sale.correo || 'No disponible') : 'N/A';
                
                const contrasena = relatedAccount.contrasena || 'No disponible';
                const perfil = sale.perfil || sale.nombrePerfil || 'No disponible';
                const pin = sale.pin || sale.pinPerfil || 'No disponible';
                const propietario = relatedAccount.propietario || 'No disponible';
                const plataforma = sale.saleType || sale.plataforma || 'Desconocido';
            
                const daysRemaining = moment(sale.endDate, 'YYYY-MM-DD').diff(today, 'days');
         
                const message = daysRemaining < 0
                    ? `*Hola 🐻,*\n\nQueremos recordarte que tu suscripción de *${plataforma}* ha vencido.\n\n*Detalles de tu cuenta:*\n\n- *Código:* \`${sale.codigo}\`\n- *Correo:* ${correo}\n- *Contraseña:* ${contrasena}\n- *Perfil:* ${perfil}\n- *PIN:* ${pin}\n\n¿Te gustaría renovarla y seguir disfrutando de tu contenido favorito?\n¡Gracias por ser parte de nuestra comunidad! 🐻`
                    : `*Hola 🐻,*\n\nQueremos recordarte que tu suscripción de *${plataforma}* está a punto de vencer.\n\n*Detalles de tu cuenta:*\n\n- *Código:* \`${sale.codigo}\`\n- *Correo:* ${correo}\n- *Contraseña:* ${contrasena}\n- *Perfil:* ${perfil}\n- *PIN:* ${pin}\n\nVence en *${Math.abs(daysRemaining)} días.* ¿Te gustaría renovarla y seguir disfrutando de tu contenido favorito?\n¡Gracias por ser parte de nuestra comunidad! 🐻`;
            
                return {
                    ...sale,
                    cuentaCorreo: correo,
                    emailUser: emailUser, // Nueva propiedad para el email del usuario
                    cuentaPlataforma: plataforma,
                    cuentaContrasena: contrasena,
                    phoneNumber: sale.phoneNumber || sale.numero || 'No disponible',
                    cantidadVendida: sale.quantity || 'No disponible',
                    mensaje: message,
                    propietario: propietario,
                    perfil: perfil,
                    pin: pin,
                    // Mantener el campo tipoVenta para mostrar en la tabla
                    tipoVenta: sale.tipoVenta
                };
            });
            
            // Ordenar por fecha de vencimiento
            relatedData.sort((a, b) => moment(a.endDate).diff(moment(b.endDate)));
            
            setSales(relatedData);
            setAccounts(accountsData);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

const handleDelete = async (sale) => {
    if (!sale.codigo) return;
    
    if (sale.cuentaEstado !== 'Vencida') {
        alert('Solo puedes eliminar ventas con estado "Vencida".');
        return;
    }
    
    const accionTexto = sale.tipoVenta === 'Online' ? 
        'eliminar esta venta online' : 
        'eliminar esta venta clásica';
        
    if (!window.confirm(`¿Estás seguro de que deseas ${accionTexto}? Esta acción es irreversible.`)) {
        return;
    }
    
    try {
        // Determinar la colección correcta basada en el tipo de venta
        const collectionName = sale.tipoVenta === 'Online' ? 'ventasOnline' : 'Ventas';
        const saleDocRef = doc(db, collectionName, sale.id);
        
        // Eliminar el documento para ambos tipos de venta
        await deleteDoc(saleDocRef);
        console.log(`${sale.tipoVenta} ${sale.id} eliminada`);

        // Update the related account's screen count para ambos tipos
        const accountDoc = accounts.find(account => account.codigo === sale.codigo);
        if (accountDoc) {
            const accountDocRef = doc(db, 'Cuentas', accountDoc.id);
            const newPantallas = (accountDoc.pantallas || 0) + (sale.quantity || sale.cantidadVendida || 0);
            await updateDoc(accountDocRef, { pantallas: Math.max(newPantallas, 0) });
            console.log(`Pantallas actualizadas para cuenta ${sale.codigo}: +${sale.quantity || sale.cantidadVendida || 0}`);
        }

        // Liberar perfiles en la colección 'profiles' para ambos tipos de venta
        try {
            const profilesQuery = query(
                collection(db, 'profiles'),
                where('codigo', '==', sale.codigo)
            );
            const profilesSnapshot = await getDocs(profilesQuery);
            
            if (!profilesSnapshot.empty) {
                const profilesDoc = profilesSnapshot.docs[0];
                const profilesData = profilesDoc.data();
                const perfiles = { ...profilesData.perfiles };
                
                // Definir los nombres de perfil y pin fuera del forEach
                const perfilNombre = sale.tipoVenta === 'Online' ? 
                    (sale.nombrePerfil || sale.perfil) : 
                    sale.perfil;
                const perfilPin = sale.tipoVenta === 'Online' ? 
                    (sale.pinPerfil || sale.pin) : 
                    sale.pin;
                
                let perfilLiberado = false;
                Object.keys(perfiles).forEach(key => {
                    const perfil = perfiles[key];
                    
                    if (perfil.displayName === perfilNombre && perfil.pin === perfilPin && perfil.vendido === true) {
                        perfiles[key].vendido = false;
                        perfilLiberado = true;
                        console.log(`Perfil liberado: ${perfil.displayName} (PIN: ${perfil.pin}) en cuenta ${sale.codigo} - Tipo: ${sale.tipoVenta}`);
                    }
                });
                
                if (perfilLiberado) {
                    await updateDoc(profilesDoc.ref, { perfiles });
                    console.log(`Perfiles actualizados para cuenta ${sale.codigo}`);
                } else {
                    console.warn(`No se encontró el perfil específico para liberar: ${perfilNombre} (PIN: ${perfilPin}) en cuenta ${sale.codigo} - Tipo: ${sale.tipoVenta}`);
                }
            } else {
                console.warn(`No se encontró información de perfiles para la cuenta ${sale.codigo}`);
            }
        } catch (profileError) {
            console.error('Error liberando perfiles:', profileError);
            // No bloquear el proceso si hay error con perfiles
        }

        alert(`${sale.tipoVenta === 'Online' ? 'Venta online' : 'Venta clásica'} eliminada correctamente. Se liberaron los cupos y perfiles en la cuenta correspondiente.`);

        // Refresh the data
        fetchExpiringData();
    } catch (error) {
        console.error('Error al procesar la venta:', error);
        alert(`Error al eliminar la ${sale.tipoVenta === 'Online' ? 'venta online' : 'venta clásica'}. Intenta nuevamente.`);
    }
};

    useEffect(() => {
        fetchExpiringData();
    }, []);

    return (
        <div style={backgroundStyle}>
            <div className='suscriptions-accounts-container'>
                <h2 className="custom-title">Suscripciones vencidas | Próximas a vencer</h2>
        
                {loading ? (
                    <p>Cargando datos...</p>
                ) : sales.length > 0 ? (
                    <div className="accounts-table-container">
                        <table className={styles['accounts-table']}>
                            <thead>
                                <tr>
                                    <th>Código</th>
                                    <th>Correo</th>
                                    <th>Email User</th>
                                    <th>Plataforma</th>
                                    <th>Contraseña</th>
                                    <th>Perfil</th>
                                    <th>PIN</th>
                                    <th>Número de Teléfono</th>
                                    <th>Cantidad</th>
                                    <th>Fecha de Corte</th>
                                    <th>Propietario</th>
                                    <th>Estado de la Cuenta</th>
                                    <th>Tipo de Venta</th>
                                    <th>Notificar</th>
                                    <th>Acción</th>
                                </tr>
                            </thead>
                            <tbody>
                            {sales.map((sale, index) => (
                                <tr
                                    key={`${sale.tipoVenta}-${sale.id}`}
                                    onClick={() => setSelectedRow(index)}
                                    className={selectedRow === index ? "selected-row" : ""}
                                >
                                    <td>{sale.codigo}</td>
                                    <td>{sale.cuentaCorreo}</td>
                                    <td>{sale.emailUser}</td>
                                    <td>{sale.cuentaPlataforma}</td>
                                    <td>{sale.cuentaContrasena}</td>
                                    <td>{sale.perfil}</td>
                                    <td>{sale.pin}</td>
                                    <td>{sale.phoneNumber}</td>
                                    <td>{sale.cantidadVendida}</td>
                                    <td>{sale.endDate}</td>
                                    <td>{sale.propietario}</td>
                                    <td data-status={sale.cuentaEstado}>
                                        {sale.cuentaEstado}
                                    </td>
                                    <td data-tipo={sale.tipoVenta.toLowerCase()}>
                                        {sale.tipoVenta}
                                    </td>
                                    <td>
                                        <a
                                            href={`https://api.whatsapp.com/send?phone=+57${sale.phoneNumber}&text=${encodeURIComponent(sale.mensaje)}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="btn cancel"
                                        >
                                            🔔
                                        </a>
                                    </td>
                                    <td>
                                        <button
                                            onClick={() => handleDelete(sale)}
                                            className="btn cancel"
                                        >
                                            ❌
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        </table>
                    </div>
                ) : (
                    <p>No se encontraron ventas próximas a vencer o vencidas.</p>
                )}
                <div className="button-group">
                    <button type="button" className={styles['transparentFullButton']} onClick={handleBack}>
                        Regresar a Inicio 🐻
                    </button>
                </div>
            </div>
        </div>
    );
}

export default ExpiringSubscriptions;